import React, { useState, useEffect } from 'react';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Department } from '@/pages/Departments';

interface DepartmentComboboxProps {
    departments: Department[];
    selectedId: string | null;
    onChange: (department: Department | null) => void;
    disabled?: boolean;
    // Optional: Allow filtering out certain departments (e.g., the one being edited)
    currentDepartmentId?: string | null;
}

const DepartmentCombobox: React.FC<DepartmentComboboxProps> = ({ departments, selectedId, onChange, disabled, currentDepartmentId }) => {
    const [query, setQuery] = useState('');

    // Filter out the current department and its children from the list of potential parents
    const availableDepartments = departments.filter(d => d.id !== currentDepartmentId && !d.completeDescription.startsWith(departments.find(dep => dep.id === currentDepartmentId)?.completeDescription + ' >'));

    const getSelectedDepartment = () => availableDepartments.find(d => d.id === selectedId) || null;

    const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);

    useEffect(() => {
        setSelectedDepartment(getSelectedDepartment());
    }, [selectedId, departments]);

    const filteredDepartments =
        query === ''
            ? availableDepartments
            : availableDepartments.filter(department =>
                department.completeDescription.toLowerCase().includes(query.toLowerCase())
            );

    return (
        <Combobox as="div" value={selectedDepartment} onChange={onChange} disabled={disabled} nullable>
            <div className="relative mt-1">
                <Combobox.Input
                    className="input-primary w-full"
                    onChange={(event) => setQuery(event.target.value)}
                    displayValue={(department: Department | null) => department?.completeDescription || ''}
                    placeholder="Nenhum (nível raiz)"
                    autoComplete="off"
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </Combobox.Button>

                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    <Combobox.Option
                        value={null}
                        className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                        }
                    >
                        <span className="block truncate italic">Nenhum (nível raiz)</span>
                    </Combobox.Option>
                    {filteredDepartments.map((department) => (
                        <Combobox.Option
                            key={department.id}
                            value={department}
                            className={({ active }) =>
                                `relative cursor-default select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                            }
                        >
                            {({ active, selected }) => (
                                <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : ''}`}>
                                        {department.completeDescription}
                                    </span>
                                    {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                    )}
                                </>
                            )}
                        </Combobox.Option>
                    ))}
                </Combobox.Options>
            </div>
        </Combobox>
    );
};

export default DepartmentCombobox; 